import React, { useEffect, useState } from "react";
import ToolShell from "./ToolShell";
import { CloudSun, Sun, Cloud, CloudRain, CloudSnow, CloudLightning } from "lucide-react";
import { fetchCities, fetchWeather } from "../../lib/api";
import { useLocale } from "../../i18n";

function iconForCode(code) {
  if ([0, 1].includes(code)) return Sun;
  if ([2, 3, 45, 48].includes(code)) return Cloud;
  if (code >= 51 && code <= 67) return CloudRain;
  if (code >= 71 && code <= 77) return CloudSnow;
  if (code >= 80 && code <= 82) return CloudRain;
  if (code >= 95 && code <= 99) return CloudLightning;
  return CloudSun;
}

const CODE_LABELS_EN = {
  0: "Clear", 1: "Mainly clear", 2: "Partly cloudy", 3: "Overcast",
  45: "Fog", 48: "Rime fog",
  51: "Light drizzle", 53: "Drizzle", 55: "Heavy drizzle",
  61: "Light rain", 63: "Rain", 65: "Heavy rain",
  71: "Light snow", 73: "Snow", 75: "Heavy snow",
  80: "Rain showers", 81: "Rain showers", 82: "Violent showers",
  95: "Thunderstorm", 96: "Storm w/ hail", 99: "Severe storm",
};
const CODE_LABELS_RO = {
  0: "Senin", 1: "Aproape senin", 2: "Parțial noros", 3: "Înnorat",
  45: "Ceață", 48: "Chiciură",
  51: "Burniță", 53: "Burniță", 55: "Burniță deasă",
  61: "Ploaie ușoară", 63: "Ploaie", 65: "Ploaie torențială",
  71: "Ninsoare ușoară", 73: "Ninsoare", 75: "Ninsoare abundentă",
  80: "Aversă", 81: "Aversă", 82: "Aversă violentă",
  95: "Furtună", 96: "Furtună cu grindină", 99: "Furtună severă",
};

const cToF = (c) => (c * 9) / 5 + 32;
const kmhToMph = (k) => k * 0.621371;

export default function WeatherTool() {
  const { t, locale } = useLocale();
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("Bucharest");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [unit, setUnit] = useState(() => {
    const saved = localStorage.getItem("wr_temp_unit");
    if (saved === "C" || saved === "F") return saved;
    return locale === "ro" ? "C" : "F";
  });

  useEffect(() => { localStorage.setItem("wr_temp_unit", unit); }, [unit]);

  useEffect(() => { fetchCities().then(setCities).catch(() => {}); }, []);
  const run = async (c = city) => {
    setLoading(true);
    try { setData(await fetchWeather(c)); } catch (e) { setData({ error: true }); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (cities.length) run(city); /* eslint-disable-next-line */ }, [cities]);

  const codeLabel = (c) => (locale === "ro" ? CODE_LABELS_RO : CODE_LABELS_EN)[c] || "";
  const CurrentIcon = data?.current?.weather_code !== undefined ? iconForCode(data.current.weather_code) : CloudSun;

  const displayTemp = (c) => {
    if (c === undefined || c === null || Number.isNaN(c)) return "—";
    const v = unit === "F" ? cToF(Number(c)) : Number(c);
    return Math.round(v);
  };
  const displayWind = (k) => {
    if (k === undefined || k === null) return "—";
    return unit === "F" ? `${Math.round(kmhToMph(k))} mph` : `${Math.round(k)} km/h`;
  };
  const unitSymbol = unit === "F" ? "°F" : "°C";

  return (
    <ToolShell
      title={t("tools.weather")}
      subtitle={t("tools.weather_sub")}
      icon={CloudSun}
      testid="tool-weather"
      seoPath="/tools/weather"
      seoTitle={locale === "ro" ? "Meteo România — prognoză 5 zile" : "Romania weather — 5-day forecast"}
      seoDescription={locale === "ro" ? "Prognoză meteo 5 zile pentru principalele orașe românești, cu comutare °C / °F." : "5-day weather forecast for major Romanian cities, with °C / °F toggle."}
    >
      <div className="mb-6 flex flex-wrap items-center gap-4 justify-between">
        <div className="flex items-center gap-4">
          <label className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink-2)]">{t("common.city")}</label>
          <select value={city} onChange={(e) => { setCity(e.target.value); run(e.target.value); }} className="font-mono-wr text-lg border border-[var(--wr-border)] bg-[var(--wr-surface)] px-3 py-2 focus:border-[var(--wr-ink)] focus:outline-none" data-testid="weather-city">
            {cities.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div
          role="radiogroup"
          aria-label="temperature unit"
          className="inline-flex items-center border border-[var(--wr-border)]"
          data-testid="weather-unit-toggle"
        >
          <button
            role="radio"
            aria-checked={unit === "C"}
            tabIndex={unit === "C" ? 0 : -1}
            onClick={() => setUnit("C")}
            onKeyDown={(e) => { if (e.key === "ArrowRight" || e.key === "ArrowLeft") setUnit(unit === "C" ? "F" : "C"); }}
            className={`px-4 py-2 font-mono-wr text-[12px] tracking-widest transition-colors focus:outline-none focus:bg-[var(--wr-surface-2)] ${
              unit === "C" ? "bg-[var(--wr-ink)] text-[var(--wr-bg)]" : "text-[var(--wr-ink)] hover:bg-[var(--wr-surface-2)]"
            }`}
            data-testid="weather-unit-c"
          >°C</button>
          <button
            role="radio"
            aria-checked={unit === "F"}
            tabIndex={unit === "F" ? 0 : -1}
            onClick={() => setUnit("F")}
            onKeyDown={(e) => { if (e.key === "ArrowLeft" || e.key === "ArrowRight") setUnit(unit === "F" ? "C" : "F"); }}
            className={`px-4 py-2 font-mono-wr text-[12px] tracking-widest transition-colors focus:outline-none focus:bg-[var(--wr-surface-2)] ${
              unit === "F" ? "bg-[var(--wr-ink)] text-[var(--wr-bg)]" : "text-[var(--wr-ink)] hover:bg-[var(--wr-surface-2)]"
            }`}
            data-testid="weather-unit-f"
          >°F</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6" data-testid="weather-result">
        <div className="lg:col-span-5 wr-sharp-card p-8 lg:p-12 bg-[var(--wr-night)] text-[var(--wr-bg)] min-h-[420px] flex flex-col justify-between">
          {loading && <p className="font-mono-wr text-white/80 text-sm">{t("common.loading")}</p>}
          {data && data.current && (
            <>
              <div>
                <p className="font-mono-wr text-[10px] uppercase tracking-widest text-white/80">{city}</p>
                <p className="font-editorial text-[110px] leading-none tracking-tight mt-4" data-testid="weather-current-temp">
                  {displayTemp(data.current.temperature_2m)}<span className="text-3xl text-white/80">{unitSymbol}</span>
                </p>
                <p className="mt-4 font-editorial text-2xl text-white/95">{codeLabel(data.current.weather_code)}</p>
              </div>
              <div className="flex items-center justify-between">
                <div className="grid grid-cols-2 gap-x-6 gap-y-2 font-mono-wr text-sm">
                  <div>
                    <p className="text-white/75 text-[10px] uppercase tracking-widest">{locale === "ro" ? "Simte ca" : "Feels like"}</p>
                    <p className="text-white text-lg">{displayTemp(data.current.apparent_temperature)}{unitSymbol}</p>
                  </div>
                  <div>
                    <p className="text-white/75 text-[10px] uppercase tracking-widest">{locale === "ro" ? "Vânt" : "Wind"}</p>
                    <p className="text-white text-lg">{displayWind(data.current.wind_speed_10m)}</p>
                  </div>
                </div>
                <CurrentIcon size={72} className="text-[var(--wr-red)]" />
              </div>
            </>
          )}
        </div>
        <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-5 gap-3">
          {data?.daily?.time?.map((day, i) => {
            const Icon = iconForCode(data.daily.weather_code[i]);
            const d = new Date(day);
            return (
              <div key={day} className="wr-sharp-card p-5 flex flex-col justify-between min-h-[180px]" data-testid={`weather-day-${i}`}>
                <p className="font-mono-wr text-[10px] uppercase tracking-widest text-[var(--wr-ink)]">
                  {d.toLocaleDateString(locale === "ro" ? "ro-RO" : "en-US", { weekday: "short", day: "numeric", month: "short" })}
                </p>
                <Icon size={30} className="text-[var(--wr-red)] my-3" />
                <div className="font-mono-wr">
                  <p className="text-2xl text-[var(--wr-ink)]">{displayTemp(data.daily.temperature_2m_max[i])}<span className="text-sm text-[var(--wr-ink-2)]">°</span></p>
                  <p className="text-sm text-[var(--wr-ink-2)]">{displayTemp(data.daily.temperature_2m_min[i])}°</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </ToolShell>
  );
}
